// Периодът и бързите превключватели над списъка.
//
// Всеки чип тук е въпрос, който админът задава наум: „какво се счупи",
// „кой пипа парите", „кой пипа настройките", „кое беше бавно". Затова са
// чипове, а не редове в падащо меню — включват се с един клик и се виждат
// включени, без да се отваря нищо.

import type { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";

import { ReportDatePicker } from "../../../../components/ReportDatePicker";
import { SLOW_MS } from "./auditTaxonomy";

export type QuickToggle =
  | "problems"
  | "money"
  | "settings"
  | "slow"
  | "reads"
  | "live";

export type QuickState = Record<QuickToggle, boolean>;

type Props = {
  dateRange: [Dayjs | null, Dayjs | null] | null;
  onDateRangeChange: (value: [Dayjs | null, Dayjs | null] | null) => void;
  /** Периодът е изключен, докато се проследява една сметка/маса. */
  dateDisabled?: boolean;
  quick: QuickState;
  onToggle: (toggle: QuickToggle) => void;
};

const chipClass = (active: boolean, tone: "brand" | "danger" | "ok") => {
  if (!active) {
    return "border-shade-3 bg-surface text-soft hover:bg-fill";
  }
  if (tone === "danger") {
    return "border-danger-tint2 bg-danger-tint text-danger-ink";
  }
  if (tone === "ok") {
    return "border-ok-tint2 bg-ok-tint text-ok-deep";
  }
  return "border-brand-tint2 bg-brand-tint text-brand-deep-ink";
};

const Chip = ({
  active,
  tone = "brand",
  onClick,
  children,
}: {
  active: boolean;
  tone?: "brand" | "danger" | "ok";
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={`inline-flex h-9 shrink-0 items-center gap-1.5 rounded-lg border px-3.5 text-[12.5px] font-bold leading-[normal] transition-colors ${chipClass(
      active,
      tone,
    )}`}
  >
    {children}
  </button>
);

export const AuditToolbar = ({
  dateRange,
  onDateRangeChange,
  dateDisabled = false,
  quick,
  onToggle,
}: Props) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-wrap items-center justify-center gap-2 overflow-x-auto pb-0.5">
      {dateDisabled ? null : (
        <ReportDatePicker
          value={dateRange}
          onChange={onDateRangeChange}
          defaultMode="day"
          withTime
        />
      )}

      <Chip
        active={quick.problems}
        tone="danger"
        onClick={() => onToggle("problems")}
      >
        {quick.problems ? "✓ " : ""}
        {t("auditLog.quick.problems")}
      </Chip>
      <Chip active={quick.money} onClick={() => onToggle("money")}>
        {quick.money ? "✓ " : ""}
        {t("auditLog.quick.money")}
      </Chip>
      <Chip active={quick.settings} onClick={() => onToggle("settings")}>
        {quick.settings ? "✓ " : ""}
        {t("auditLog.quick.settings")}
      </Chip>
      <Chip active={quick.slow} onClick={() => onToggle("slow")}>
        {quick.slow ? "✓ " : ""}
        {t("auditLog.quick.slow", { ms: SLOW_MS })}
      </Chip>
      <Chip active={quick.reads} onClick={() => onToggle("reads")}>
        {quick.reads ? "✓ " : ""}
        {t("auditLog.quick.reads")}
      </Chip>
      <Chip active={quick.live} tone="ok" onClick={() => onToggle("live")}>
        <span
          aria-hidden
          className={`size-[7px] rounded-full ${
            quick.live ? "animate-pulse bg-ok-solid" : "bg-ghost"
          }`}
        />
        {t("auditLog.quick.live")}
      </Chip>
    </div>
  );
};
